import { useState, useEffect } from 'react';
import { Shield, Users, Lock, Plus, X, Check, Edit2, Trash2, Search } from 'lucide-react';
import { useAppSelector } from '../store';
import { cn, API_BASE_URL } from '../lib/utils';
import { DEFAULT_ROLE_PERMISSIONS, PERMISSION_CATEGORIES, PERMISSION_DESCRIPTIONS } from '../config/permissions';
import PermissionGuard from '../components/shared/PermissionGuard';
import type { UserRole, Permission, RolePermissions, User } from '../types';

export default function RoleManagementPage() {
  const { token } = useAppSelector((state) => state.auth);
  const [roles, setRoles] = useState<RolePermissions[]>(Object.values(DEFAULT_ROLE_PERMISSIONS));
  const [users, setUsers] = useState<User[]>([]);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [editingPermissions, setEditingPermissions] = useState<Permission[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'roles' | 'users'>('roles');

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    const rolePerms = roles.find(r => r.role === role);
    setEditingPermissions(rolePerms?.permissions || []);
    setIsEditing(false);
  };

  const handlePermissionToggle = (permission: Permission) => {
    if (editingPermissions.includes(permission)) {
      setEditingPermissions(editingPermissions.filter(p => p !== permission));
    } else {
      setEditingPermissions([...editingPermissions, permission]);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      const res = await fetch(`${API_BASE_URL}/roles/${selectedRole}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ permissions: editingPermissions }),
      });

      if (res.ok) {
        setRoles(roles.map(r => 
          r.role === selectedRole 
            ? { ...r, permissions: editingPermissions, isCustom: true }
            : r
        ));
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Failed to save permissions:', error);
    }
  };

  const handleResetToDefault = () => {
    if (!selectedRole) return;
    const defaultPerms = DEFAULT_ROLE_PERMISSIONS[selectedRole];
    setEditingPermissions(defaultPerms.permissions);
  };

  const handleUpdateUserRole = async (userId: string, newRole: UserRole) => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (res.ok) {
        setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      }
    } catch (error) {
      console.error('Failed to update user role:', error);
    }
  };

  const selectedRoleData = roles.find(r => r.role === selectedRole);
  const filteredUsers = users.filter(u => 
    u.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleColor = (role: UserRole) => {
    if (role === 'system_admin') return 'text-red-500 bg-red-500/10 border-red-500/20';
    if (role.includes('admin')) return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
    if (role.includes('supervisor') || role.includes('commander')) return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    if (role.includes('dispatcher') || role.includes('coordinator')) return 'text-cyan-500 bg-cyan-500/10 border-cyan-500/20';
    if (role.includes('officer') || role.includes('analyst')) return 'text-green-500 bg-green-500/10 border-green-500/20';
    return 'text-muted-foreground bg-accent border-border';
  };

  return (
    <PermissionGuard permission="system:manage_roles" fallback={
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Access Denied</h2>
          <p className="text-sm text-muted-foreground">You don't have permission to manage roles</p>
        </div>
      </div>
    }>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-border bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">Role & Permission Management</h1>
                <p className="text-xs text-muted-foreground">Configure user roles and access control</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('roles')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                activeTab === 'roles'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-accent text-muted-foreground hover:text-foreground'
              )}
            >
              <Shield className="w-4 h-4 inline-block mr-2" />
              Roles & Permissions
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                activeTab === 'users'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-accent text-muted-foreground hover:text-foreground'
              )}
            >
              <Users className="w-4 h-4 inline-block mr-2" />
              User Assignments
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {activeTab === 'roles' ? (
            <div className="h-full grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
              {/* Roles List */}
              <div className="glass-panel p-4 overflow-y-auto">
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  System Roles ({roles.length})
                </h2>
                <div className="space-y-2">
                  {roles.map((role) => (
                    <button
                      key={role.role}
                      onClick={() => handleRoleSelect(role.role)}
                      className={cn(
                        'w-full text-left p-3 rounded-lg border transition-all',
                        selectedRole === role.role
                          ? 'bg-primary/10 border-primary'
                          : 'bg-card border-border hover:border-primary/50'
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full border font-medium', getRoleColor(role.role))}>
                          {role.role.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        {role.isCustom && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-500 border border-yellow-500/20">
                            CUSTOM
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{role.description}</p>
                      <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground">
                        <Lock className="w-3 h-3" />
                        {role.permissions.length} permissions
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Permissions Editor */}
              <div className="lg:col-span-2 glass-panel flex flex-col">
                {selectedRoleData ? (
                  <>
                    {/* Header */}
                    <div className="p-4 border-b border-border flex-shrink-0">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h2 className="text-sm font-semibold text-foreground">
                            {selectedRoleData.role.replace(/_/g, ' ').toUpperCase()}
                          </h2>
                          <p className="text-xs text-muted-foreground">{selectedRoleData.description}</p>
                        </div>
                        <div className="flex gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleResetToDefault}
                                className="px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-accent transition-colors"
                              >
                                Reset
                              </button>
                              <button
                                onClick={() => setIsEditing(false)}
                                className="px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-accent transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                              <button
                                onClick={handleSavePermissions}
                                className="px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                              >
                                <Check className="w-3 h-3 inline-block mr-1" />
                                Save
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setIsEditing(true)}
                              className="px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                            >
                              <Edit2 className="w-3 h-3 inline-block mr-1" />
                              Edit
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">
                          {editingPermissions.length} of {Object.keys(PERMISSION_DESCRIPTIONS).length} permissions
                        </span>
                        {selectedRoleData.isCustom && (
                          <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500 border border-yellow-500/20">
                            Modified
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Permissions Grid */}
                    <div className="flex-1 overflow-y-auto p-4">
                      <div className="space-y-4">
                        {Object.entries(PERMISSION_CATEGORIES).map(([category, permissions]) => (
                          <div key={category} className="border border-border rounded-lg p-3">
                            <h3 className="text-xs font-semibold text-foreground mb-2">{category}</h3>
                            <div className="space-y-1">
                              {permissions.map((permission) => {
                                const hasPermission = editingPermissions.includes(permission as Permission);
                                return (
                                  <label
                                    key={permission}
                                    className={cn(
                                      'flex items-start gap-2 p-2 rounded cursor-pointer transition-colors',
                                      isEditing ? 'hover:bg-accent' : 'cursor-default',
                                      hasPermission && 'bg-primary/5'
                                    )}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={hasPermission}
                                      onChange={() => isEditing && handlePermissionToggle(permission as Permission)}
                                      disabled={!isEditing}
                                      className="mt-0.5 rounded border-border"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs font-medium text-foreground">
                                        {permission.split(':')[1].replace(/_/g, ' ')}
                                      </div>
                                      <div className="text-[10px] text-muted-foreground">
                                        {PERMISSION_DESCRIPTIONS[permission as Permission]}
                                      </div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                      <p className="text-sm text-muted-foreground">Select a role to view permissions</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full p-4">
              <div className="glass-panel h-full flex flex-col">
                {/* Search */}
                <div className="p-4 border-b border-border flex-shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search users by name, email, or role..."
                      className="w-full pl-10 pr-4 py-2 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                {/* Users Table */}
                <div className="flex-1 overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-card border-b border-border">
                      <tr>
                        <th className="text-left p-3 text-xs font-semibold text-muted-foreground">User</th>
                        <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Department</th>
                        <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Current Role</th>
                        <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Status</th>
                        <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="border-b border-border hover:bg-accent/50 transition-colors">
                          <td className="p-3">
                            <div>
                              <div className="text-sm font-medium text-foreground">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-xs text-muted-foreground">{user.email}</div>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="text-xs text-foreground">{user.department}</span>
                          </td>
                          <td className="p-3">
                            <span className={cn('text-xs px-2 py-1 rounded-full border font-medium', getRoleColor(user.role))}>
                              {user.role.replace(/_/g, ' ').toUpperCase()}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={cn(
                              'text-xs px-2 py-1 rounded-full border font-medium',
                              user.isOnline
                                ? 'text-green-500 bg-green-500/10 border-green-500/20'
                                : 'text-muted-foreground bg-accent border-border'
                            )}>
                              {user.isOnline ? 'Online' : 'Offline'}
                            </span>
                          </td>
                          <td className="p-3">
                            <select
                              value={user.role}
                              onChange={(e) => handleUpdateUserRole(user.id, e.target.value as UserRole)}
                              className="text-xs px-2 py-1 rounded border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                            >
                              {roles.map((role) => (
                                <option key={role.role} value={role.role}>
                                  {role.role.replace(/_/g, ' ').toUpperCase()}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                        <p className="text-sm text-muted-foreground">No users found</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PermissionGuard>
  );
}
